import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Store, 
  Users, 
  ClipboardList, 
  Settings, 
  LogOut,
  Menu,
  X,
  BarChart3,
  AlertTriangle,
  MessageSquareWarning,
  Activity,
  MessageCircle,
  UserPlus,
  Shield,
  Mail,
  Receipt,
  UserCheck,
  ToggleLeft
} from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { path: '/admin', label: 'Tableau de bord', icon: LayoutDashboard },
  { path: '/admin/restaurants', label: 'Restaurants', icon: Store },
  { path: '/admin/onboarding', label: 'Inscriptions', icon: UserPlus, highlight: true },
  { path: '/admin/users', label: 'Utilisateurs', icon: Users },
  { path: '/admin/orders', label: 'Commandes', icon: ClipboardList },
  { path: '/admin/chat', label: 'Messages', icon: MessageCircle, showBadge: true },
  { path: '/admin/disputes', label: 'Litiges', icon: AlertTriangle },
  { path: '/admin/reviews', label: 'Modération Avis', icon: MessageSquareWarning },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/weekly-reports', label: 'Rapports Hebdo', icon: Mail },
  { path: '/admin/commissions', label: 'Commissions', icon: Receipt },
  { path: '/admin/leads', label: 'Leads Partenaires', icon: UserCheck, highlight: true },
  { path: '/admin/audit', label: 'Journal d\'Audit', icon: Shield },
  { path: '/admin/health', label: 'Santé Système', icon: Activity },
  { path: '/admin/emails', label: 'Apercu Emails', icon: Mail },
  { path: '/admin/email-settings', label: 'Controle Emails', icon: ToggleLeft },
  { path: '/admin/settings', label: 'Paramètres', icon: Settings },
];

export const AdminSidebar = ({ className = '' }) => {
  const location = useLocation();
  const { logout, user, token } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread chat messages count for admin
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/chat/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(response.data.unread_count || 0);
      } catch (error) {
        // Silently fail
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000); // Check every 10 seconds
    
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className={`bg-gray-900 text-white w-64 min-h-screen flex flex-col ${className}`}>
      {/* Logo */}
      <div className="p-4 border-b border-gray-800">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="LEMO" className="w-14 h-14 -my-2 object-contain" />
        </Link>
        <span className="text-xs text-gray-400 mt-1 block">Administration</span>
      </div>

      {/* User Info */}
      <div className="px-4 py-2.5 border-b border-gray-800">
        <p className="text-[11px] text-gray-400">Connecte en tant que</p>
        <p className="text-sm font-semibold truncate">{user?.name}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <ul className="space-y-0.5">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                    isActive 
                      ? 'bg-emerald-500 text-white' 
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                  {item.showBadge && unreadCount > 0 && (
                    <span className="absolute right-2 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-gray-800">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-4 py-3 text-gray-300 hover:bg-gray-800 hover:text-white rounded-xl font-medium transition-colors"
        >
          <LogOut className="w-5 h-5" />
          Déconnexion
        </button>
      </div>
    </aside>
  );
};

export const AdminLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Desktop Sidebar */}
      <AdminSidebar className="hidden lg:flex fixed left-0 top-0 h-full" />

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 bg-gray-900 text-white">
        <div className="flex items-center justify-between px-4 h-16">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-gray-800 rounded-lg"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="LEMO" className="w-16 h-16 -my-2 object-contain" />
          </Link>
          <div className="w-10" />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-64 animate-slide-in-right">
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-800 rounded-lg text-white z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <AdminSidebar />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-4 lg:p-8">
          {title && (
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6">{title}</h1>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};
